import { useCallback } from 'react';
import Select, { FilterOptionOption } from 'react-select';
import { useFormContext, Controller } from 'react-hook-form';
import { makeSelectOption, makeSelectOptions } from '@utils/makeSelectOptions';
import StarIcon from '@icons/star_required_icon.svg?component';
import { SingleValue } from './SingleValue';
import { MenuList } from './MenuList';
import { getDropdownIndicator } from './DrpodownIndicator';
import { MultiValueRemove } from './MultiValueRemove';
import { MultiValue } from './MultiValue';
import { Option } from './Option';
import { getCustomStyles } from './selectCustomStyles';
import { Obj } from '@/types/common';
import { ISelectComponentProps } from './types';

export function SelectComponent<T extends Obj>({
  name,
  options,
  onInputChange,
  onSelect,
  onMultiSelect,
  customOption,
  customSingleValue,
  customMultiValue,
  validation,
  isClearable,
  label,
  styleProps,
  maxMenuHeight,
  optionLabel,
  optionValue,
  showRequiredIcon = false,
  isMulti = false,
  required = false,
  isSearchSelect = false,
  isLoading = false,
  disableFiltering = false,
  menuPlacement = 'top',
  multiValueRemoveOptions,
  multiValueStyleOptions,
  placeholder,
  ...props
}: ISelectComponentProps<T>) {
  const formContext = useFormContext();

  const { control } = formContext;

  const filterOptions = useCallback(
    (candidate: FilterOptionOption<T>, input: string) => {
      return disableFiltering ? true : candidate.label.toLowerCase().includes(input.toLowerCase());
    },
    [disableFiltering]
  );

  const getSelectValue = (value: any | any[]) => {
    if (!value) return undefined;
    const val = isMulti ? value[0] : value;
    return typeof val !== 'object'
      ? (isMulti ? makeSelectOptions : makeSelectOption)(
          value,
          optionValue && optionLabel
            ? {
                value: optionValue as string,
                label: optionLabel as string,
              }
            : undefined
        )
      : value;
  };

  const onInputChangeHandler = useCallback(
    (val: string) => {
      if (onInputChange) {
        onInputChange(val);
      }
    },
    [onInputChange]
  );

  return (
    <div className="relative flex flex-col items-start justify-center w-full">
      {label && (
        <div className="label">
          <p>{label}</p>
          {required && showRequiredIcon && <StarIcon width={12} height={12} />}
        </div>
      )}
      <div className="relative block w-full group">
        <Controller
          control={control}
          name={name}
          rules={{ required: required, validate: validation?.validate }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <Select
                id={name}
                instanceId={name}
                styles={getCustomStyles<T>({
                  isError: !!error,
                  controlStyles: styleProps,
                  multiValueStyleOptions,
                })}
                options={options as T[]}
                getOptionLabel={optionLabel ? (val) => val[optionLabel] : undefined}
                getOptionValue={optionValue ? (val) => val[optionValue] : undefined}
                filterOption={filterOptions}
                value={getSelectValue(value) as any}
                onInputChange={onInputChangeHandler}
                onChange={(val) => {
                  if (isMulti && onMultiSelect) onMultiSelect((val || []) as T[]);
                  else if (onSelect) onSelect(val as T);
                  else onChange(val);
                }}
                isClearable={isClearable === undefined ? isMulti || !required : isClearable}
                components={{
                  MenuList: (props) => MenuList(props, optionValue, isLoading, maxMenuHeight),
                  Option: (props) => Option(props, optionValue, customOption),
                  DropdownIndicator: (props) => getDropdownIndicator(props, isSearchSelect),
                  MultiValueRemove: (props) => MultiValueRemove(props, multiValueRemoveOptions),
                  MultiValueLabel: (props) => MultiValue(props, customMultiValue),
                  SingleValue: (props) => SingleValue(props, customSingleValue),
                }}
                isMulti={isMulti}
                menuPlacement={menuPlacement}
                placeholder={placeholder}
                aria-label={label || placeholder || name}
                aria-describedby={`${name}-help`}
                {...props}
                // menuIsOpen
              />
              <span id={`${name}-help`} className="sr-only">
                {name} help text
              </span>
            </>
          )}
        />
      </div>
    </div>
  );
}
